import { z } from "zod/v4";

export const SEGMENT_PERSONAS = [
  "BUYER",
  "SELLER",
  "INVESTOR",
  "OWNER",
  "BROKER_BUY_SIDE",
  "BROKER_SELL_SIDE",
] as const;

export type SegmentPersona = (typeof SEGMENT_PERSONAS)[number];

export const SEGMENT_CONDITION_FIELDS = [
  "persona",
  "industryCategoryId",
  "businessTypeId",
  "subdivisionId",
  "budgetMin",
  "budgetMax",
  "timeline",
  "experience",
  "hasCompletedIntake",
  "lastActivityAt",
  "dealStage",
  "revenueBand",
] as const;

export type SegmentConditionField = (typeof SEGMENT_CONDITION_FIELDS)[number];

export const SEGMENT_CONDITION_OPS = [
  "eq",
  "neq",
  "in",
  "notIn",
  "gte",
  "lte",
  "lt",
  "gt",
  "isTrue",
  "isFalse",
  "beforeDaysAgo",
  "afterDaysAgo",
] as const;

export type SegmentConditionOp = (typeof SEGMENT_CONDITION_OPS)[number];

export const segmentConditionSchema = z.object({
  field: z.enum(SEGMENT_CONDITION_FIELDS),
  op: z.enum(SEGMENT_CONDITION_OPS),
  value: z.unknown().optional(),
});

export type SegmentCondition = z.infer<typeof segmentConditionSchema>;

export const segmentRuleSchema = z.object({
  version: z.literal(1),
  match: z.enum(["all", "any"]).default("all"),
  conditions: z.array(segmentConditionSchema).default([]),
  excludeContactIds: z.array(z.string()).default([]),
  suggestionKey: z.string().optional(),
});

export type SegmentRule = z.infer<typeof segmentRuleSchema>;

export function parseSegmentRule(value: unknown): SegmentRule | null {
  const parsed = segmentRuleSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function personaCondition(persona: SegmentPersona): SegmentCondition {
  return { field: "persona", op: "eq", value: persona };
}

export function buildPersonaRule(
  persona: SegmentPersona,
  suggestionKey: string,
): SegmentRule {
  return {
    version: 1,
    match: "all",
    conditions: [personaCondition(persona)],
    excludeContactIds: [],
    suggestionKey,
  };
}
